import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Badge,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import MapIcon from '@mui/icons-material/Map';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import WarningIcon from '@mui/icons-material/Warning';
import { usePlace, usePlaceScoreHistory, useKeywords } from '../../hooks';
import { KeywordGenerationModal } from '@/components/features/keyword';
import { keywordService } from '@/services';
import type { KeywordListWithSummary, PlaceKeywordSettings } from '@/types';

export function PlaceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const placeId = Number(id);
  const navigate = useNavigate();

  const { place, isLoading: isPlaceLoading, error: placeError, fetchPlace, deletePlace } = usePlace();
  const { fetchHistory } = usePlaceScoreHistory();
  const { keywords, isLoading: isKeywordsLoading, fetchKeywords } = useKeywords();
  const [generationModalOpen, setGenerationModalOpen] = useState(false);
  const [keywordsWithSummary, setKeywordsWithSummary] = useState<KeywordListWithSummary | null>(null);
  const [settings, setSettings] = useState<PlaceKeywordSettings | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    if (placeId) {
      fetchPlace(placeId);
      fetchHistory(placeId);
      fetchKeywords(placeId);
      loadKeywordsWithSummary();
      loadSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId, fetchPlace, fetchHistory, fetchKeywords]);

  const loadKeywordsWithSummary = async () => {
    try {
      const data = await keywordService.listKeywordsWithSummary(placeId);
      setKeywordsWithSummary(data);
    } catch {
      console.error('Failed to load keywords with summary');
    }
  };

  const loadSettings = async () => {
    try {
      const data = await keywordService.getKeywordSettings(placeId);
      setSettings(data);
    } catch {
      console.error('Failed to load settings');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('정말 이 플레이스를 삭제하시겠습니까?')) {
      await deletePlace(placeId);
      navigate('/places');
    }
  };

  const handleKeywordClick = (keywordId: number) => {
    navigate(`/keywords/${keywordId}`);
  };

  const handleGenerationComplete = () => {
    fetchKeywords(placeId);
    loadKeywordsWithSummary();
  };

  const handleCopyAllKeywords = async () => {
    try {
      const text = await keywordService.copyAllKeywords(placeId);
      await navigator.clipboard.writeText(text);
      setSnackbarMessage('키워드가 복사되었습니다');
      setSnackbarOpen(true);
    } catch {
      setSnackbarMessage('복사에 실패했습니다');
      setSnackbarOpen(true);
    }
  };

  const handleCopyUrl = async (keyword: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const url = `https://m.search.naver.com/search.naver?query=${encodeURIComponent(keyword)}&sm=mtb_hty.top&ssc=tab.m.all`;
    await navigator.clipboard.writeText(url);
    setSnackbarMessage('URL이 복사되었습니다');
    setSnackbarOpen(true);
  };

  const handleExport = async () => {
    try {
      const blob = await keywordService.exportKeywords(placeId, 'csv', true);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `keywords_${placeId}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      setSnackbarMessage('내보내기에 실패했습니다');
      setSnackbarOpen(true);
    }
  };

  const rankThreshold = settings?.rank_threshold || 10;

  if (isPlaceLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (placeError || !place) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{placeError || '플레이스를 찾을 수 없습니다.'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/places')}
          sx={{ mb: 2, color: 'text.secondary' }}
        >
          목록으로 돌아가기
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
                {place.name}
              </Typography>
              <Chip
                label={place.place_type.toUpperCase()}
                color="primary"
                variant="outlined"
                size="small"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                label={place.is_active ? 'Active' : 'Inactive'}
                color={place.is_active ? 'success' : 'default'}
                size="small"
              />
            </Box>
            <Typography variant="subtitle1" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapIcon fontSize="small" />
              {place.address.road || place.address.jibun || '주소 정보 없음'}
            </Typography>
          </Box>
          <Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
            >
              삭제
            </Button>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ height: '100%', border: '1px solid #e9ecef', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>기본 정보</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">카테고리</Typography>
                  <Typography variant="body1">{place.category} / {place.main_category}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">전화번호</Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    {place.phone || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">네이버 URL</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {place.naver_url ? (
                      <a href={place.naver_url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                        {place.naver_url}
                      </a>
                    ) : '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">등록일</Typography>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTimeIcon fontSize="small" color="action" />
                    {new Date(place.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {[
              { label: 'Score 1', value: place.gdid_scores.score1, color: '#e3f2fd', text: '#1565c0' },
              { label: 'Score 2', value: place.gdid_scores.score2, color: '#e8f5e9', text: '#2e7d32' },
              { label: 'Score 3', value: place.gdid_scores.score3, color: '#fff3e0', text: '#ef6c00' },
            ].map((score) => (
              <Grid item xs={4} key={score.label}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    bgcolor: score.color,
                    borderRadius: 3,
                    height: '100%'
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">{score.label}</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: score.text, mt: 1 }}>
                    {score.value?.toLocaleString() ?? '-'}
                  </Typography>
                </Paper>
              </Grid>
            ))}

            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: 300,
                  bgcolor: '#f8f9fa',
                  border: '1px dashed #ced4da',
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column'
                }}
              >
                <TrendingUpIcon sx={{ fontSize: 48, color: '#adb5bd', mb: 2 }} />
                <Typography color="text.secondary" variant="h6">
                  GDID 히스토리 차트
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  (Chart Component Placeholder)
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                키워드 목록
              </Typography>
              <Chip label={`${keywords?.total || 0}개`} size="small" />
              {keywordsWithSummary && keywordsWithSummary.summary.rank_exceeded_count > 0 && (
                <Badge badgeContent={keywordsWithSummary.summary.rank_exceeded_count} color="error">
                  <Chip
                    icon={<WarningIcon />}
                    label={`${rankThreshold}위 미달`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </Badge>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyAllKeywords}
              >
                전체 복사
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon />}
                onClick={handleExport}
              >
                내보내기
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setGenerationModalOpen(true)}
              >
                키워드 생성
              </Button>
            </Box>
          </Box>
          <Paper elevation={1} sx={{ overflow: 'hidden', borderRadius: 2 }}>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>키워드</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>유형</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>검색어</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }} align="right">현재 순위</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }} align="right">변동</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa' }}>마지막 확인</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f8f9fa', width: 60 }} align="center">URL</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isKeywordsLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell>
                    </TableRow>
                  ) : keywords?.data.map((kw) => {
                    const isExceeded = kw.current_rank !== null && kw.current_rank > rankThreshold;
                    return (
                      <TableRow
                        key={kw.id}
                        hover
                        onClick={() => handleKeywordClick(kw.id)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: isExceeded ? 'error.lighter' : 'inherit',
                          '&:hover': {
                            bgcolor: isExceeded ? '#ffebee' : undefined,
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>{kw.keyword}</TableCell>
                        <TableCell>
                          <Chip label={kw.keyword_type} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{kw.search_query || '-'}</TableCell>
                        <TableCell align="right">
                          <Box sx={{
                            fontWeight: 'bold',
                            color: (kw.current_rank || 999) <= rankThreshold ? 'success.main' : 'error.main'
                          }}>
                            {kw.current_rank ? `${kw.current_rank}위` : '-'}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{
                           color: (kw.rank_change || 0) > 0 ? 'error.main' : (kw.rank_change || 0) < 0 ? 'success.main' : 'text.secondary'
                        }}>
                          {kw.rank_change ? (kw.rank_change > 0 ? `▲${kw.rank_change}` : `▼${Math.abs(kw.rank_change)}`) : '-'}
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                          {kw.last_checked_at ? new Date(kw.last_checked_at).toLocaleString() : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="URL 복사">
                            <IconButton size="small" onClick={(e) => handleCopyUrl(kw.keyword, e)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!isKeywordsLoading && (!keywords || keywords.data.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                        등록된 키워드가 없습니다.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <KeywordGenerationModal
        open={generationModalOpen}
        placeId={placeId}
        onClose={() => setGenerationModalOpen(false)}
        onComplete={handleGenerationComplete}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
}
